import { getAuth, signOut } from 'firebase/auth'
import { NavigateFunction } from 'react-router'
import { clearReduxStateData } from './clear-user-data'
import postLogout from './post-logout'
import { forgetSignedInUser } from './signed-in-marker'
import { kSessionStorageActiveMatterIdKey } from '@/matter/constants'
import { kSessionStorageSubscriptionNoticeBannerDismissedKey } from '@/account/constants'
import { clearAllRecentSearches } from '@/user_files/search/recent-searches-storage'
import { clearAttachedSkillNames } from '@/user-skills/utils/attached-skill-storage'
import { clearOutlookLocalData } from '@/outlook-addin/auth/logout'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { GlobalToastType } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { store } from '@/store/store'

/**
 * Sign Out Of App
 * Revokes the user's sessions on the server, then signs out of Firebase in this browser.
 * A failed revocation still signs out here but tells the user it did not reach every device.
 */
export default async function signOutOfApp(navigate?: NavigateFunction) {
  const auth = getAuth()
  const showRefreshedAuthPages = kGetFeatureFlagStatus(kFeatureFlags.ONBOARDING_REFRESH_AUTH_PAGES)

  let revocationFailed = false
  try {
    await postLogout()
  } catch {
    revocationFailed = true
  }

  try {
    clearAllRecentSearches()
    clearAttachedSkillNames()
    clearOutlookLocalData()
    sessionStorage.removeItem(kSessionStorageActiveMatterIdKey)
    sessionStorage.removeItem(kSessionStorageSubscriptionNoticeBannerDismissedKey)
  } finally {
    // A sign-out started here must not show the session-ended modal.
    forgetSignedInUser()
    try {
      // Unavailable browser storage must not prevent Firebase from clearing its auth persistence.
      await signOut(auth)
    } finally {
      // Resetting requests to idle can wake mounted consumers. Remove the old identity first.
      await clearReduxStateData()
    }
  }

  if (navigate) navigate(showRefreshedAuthPages ? '/sign-in' : '/dashboard')

  if (revocationFailed) {
    store.dispatch(
      openGlobalToast({
        type: GlobalToastType.ERROR,
        message: 'Signed out on this device. Your other sessions could not be ended.',
      })
    )
  }
}
